import { RouteErrorComponent } from "$/components/route-error-component"
import { readDeviceLanguage } from "$/lib/language-preference"
import translationQueries from "$/lib/queries/translation-queries"
import { userQueryOptions } from "$/lib/queries/user-queries"
import { Language } from "@repo/common"
import { TranslationIntlProvider } from "@repo/ui/components/translation-intl-provider"
import { Button } from "@repo/ui/components/ui/button"
import { Body, H1 } from "@repo/ui/components/ui/typography"
import { useQuery } from "@tanstack/react-query"
import { ErrorComponentProps, useRouter } from "@tanstack/react-router"

// Shown when the texts themselves failed to load, so they can't come from
// the dictionary
const FALLBACK_TEXTS: Record<
  Language,
  { title: string; description: string; retry: string }
> = {
  sv: {
    title: "Något gick fel.",
    description:
      "Försök igen. Om det fortfarande inte fungerar, starta om webbläsaren eller skicka ett mejl till rasmus@julo.se",
    retry: "Försök igen",
  },
  en: {
    title: "Something went wrong.",
    description:
      "Please try again. If it still does not work, restart your browser or send an email to rasmus@julo.se",
    retry: "Try again",
  },
}

/**
 * The root route's error screen. It renders in place of the root component,
 * outside the TranslationProvider, and the error may be that the texts
 * failed to load.
 */
export const RootErrorComponent = ({ error }: ErrorComponentProps) => {
  const router = useRouter()
  // Reads whatever is already cached; never fetches on its own
  const { data: user } = useQuery({ ...userQueryOptions, enabled: false })
  const language = user?.language ?? readDeviceLanguage()
  const { data: dictionary } = useQuery({
    ...translationQueries.dictionary(language),
    enabled: false,
  })

  if (dictionary) {
    return (
      <TranslationIntlProvider
        language={language}
        messages={dictionary.messages}
      >
        <RouteErrorComponent error={error} />
      </TranslationIntlProvider>
    )
  }

  const texts = FALLBACK_TEXTS[language]

  // Re-runs the root loader, so a passing network blip recovers
  const handleRetry = () => {
    router.invalidate()
  }

  return (
    <div className="flex flex-col items-center justify-center size-full gap-2 p-6 text-center md:gap-4">
      <div className="space-y-2">
        <H1 weight="bold" className="text-3xl sm:text-4xl">
          {texts.title}
        </H1>
        <Body className="text-muted-foreground">{texts.description}</Body>
      </div>
      <Button onClick={handleRetry}>{texts.retry}</Button>
    </div>
  )
}
